import { apiClient } from './client';
import { avatarImageUrl } from '../lib/avatar-src';
import { registerSessionCache } from '../lib/session-caches';

/**
 * Project Team API client (project-menu-detailing) — backend
 * `apps/chat-server/src/modules/tasks/project-teams.routes.ts` 와 1:1.
 * 계약: docs/design/interface-project-menu.md §1.
 *
 * avatarUrl: 서버는 DB raw 값(`wcavatar:{ts}` 센티널 또는 외부 URL)을 그대로 내려준다 —
 * 본 클라이언트가 avatarImageUrl 로 표시용 URL 로 변환해 반환 (컴포넌트는 <img src> 직결).
 */

function resolveMemberAvatar<T extends { userId: string; avatarUrl: string | null }>(row: T): T {
  return { ...row, avatarUrl: avatarImageUrl(row.userId, row.avatarUrl) };
}

export type ProjectTeamRole = 'LEADER' | 'MEMBER';

export interface TeamDto {
  id: string;
  name: string;
  description: string | null;
  myRole: ProjectTeamRole | 'ADMIN_BYPASS' | null;
  memberCount: number;
  projectCount: number;
  createdBy: string;
  createdAt: string;
  /**
   * ⭐ 2026-10-08 — 팀(스페이스)이 **고른 색**. DB 칸은 `ProjectTeam.color VarChar(9)` 이고
   * 서버가 ★ 네 끝점 «모두»에서 실어 준다(`project-teams.routes.ts` · `color: true` ×4).
   *
   * ⚠ `null` 일 수 있다 — 그때는 ★ `stripColor(id)` 해시 색으로 그린다.
   *   ⇒ ⭐ 그 규칙은 ★ 이 레포가 이미 적어 두었다(`api/tasks.ts` 의 `color` 칸 머리말과 ★ 같은 꼴).
   * ⚠ `?:` 로 둔 까닭은 `TaskProjectDto.myRole` 과 ★ 같다(그 머리말 참조).
   */
  color?: string | null;
}

export interface TeamMemberDto {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: ProjectTeamRole;
  addedAt: string;
}

export interface CandidateDto {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface CreateTeamInput {
  name: string;
  description?: string;
  /**
   * 스페이스 색(2026-08-19 생성 화면). 서버가 `project_teams.color` 에 저장한다.
   *
   * ## ⭐ 2026-10-10 — ★ 서버는 ⭐ «이미 받는다». ⇒ 이 줄은 ★ «받는 쪽»만 메운다
   *
   * ```
   * apps/chat-server/.../tasks/project-teams.routes.ts:59
   *   color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' }     ⇐ ★ ajv 스키마
   * 같은 파일 :227 ⇒ color: body.color ?? null                     ⇐ ★ 저장한다
   * ⇒ ⇒ ⭐ 그러니 ★ 서버는 ⛔ 한 글자도 안 건드렸다.
   * ```
   *
   * ⚠⚠ **7자여야 한다** — ⇒ ★ 8자(`#RRGGBBAA`)를 보내면 ⭐ ajv 가 ★ 400 을 준다.
   *   ⇒ ⇒ 보내는 쪽(`SpaceCreateModal`)은 ★ `GROUP_COLORS` 를 쓰고 ⇒ ⭐ 그 열두 개가 7자임을
   *      ⇒ ⇒ ★ `pages/ProjectPage/canon-layer2.guard.test.ts ④` 가 ⭐ 잠근다.
   * ⚠ 그리고 ★ 이 칸을 ⭐ 지우면 ⇒ ★ `SpaceCreateModal.tsx:53` 에서 ⭐ **tsc 가 울린다**(TS2353)
   *   ⇒ ⇒ ★ 즉 ⭐ 이 줄의 잠금은 ★ «시험»이 아니라 ⭐ tsc 가 쥔다 — ⇒ 사보타주로 확인했다.
   *   ⇒ ⇒ ⚠ 까닭 ⇒ ★ 리터럴로 넘기므로 ⭐ «남는-칸 검사»에 걸린다. ⇒ ⇒ ⛔ 펼쳐서(`...`) 넘기면
   *      ⇒ ⇒ ⇒ ★ 그 검사를 빠져나간다 — ⇒ 이 레포가 적어 둔 그 함정이다.
   */
  color?: string;
}

export interface PatchTeamInput {
  name?: string;
  description?: string;
}

export async function listTeams(): Promise<TeamDto[]> {
  const res = await apiClient.get<{ teams: TeamDto[] }>('/project-teams');
  return Array.isArray(res.data?.teams) ? res.data.teams : [];
}

export async function createTeam(input: CreateTeamInput): Promise<TeamDto> {
  const res = await apiClient.post<{ team: TeamDto }>('/project-teams', input);
  return res.data.team;
}

export async function getTeam(id: string): Promise<TeamDto> {
  const res = await apiClient.get<{ team: TeamDto }>(`/project-teams/${id}`);
  return res.data.team;
}

export async function patchTeam(id: string, input: PatchTeamInput): Promise<TeamDto> {
  const res = await apiClient.patch<{ team: TeamDto }>(`/project-teams/${id}`, input);
  return res.data.team;
}

export async function deleteTeam(id: string): Promise<void> {
  await apiClient.delete(`/project-teams/${id}`);
}

/**
 * 팀 멤버 조회 — **60초 TTL 캐시 + in-flight 중복 제거** (2026-07-25 요청량 진단).
 *
 * 같은 팀의 멤버를 사이드패널·팀 간트·팀 화면이 각자 부르고, 화면을 옮길 때마다 다시 부른다.
 * 실측: `/task?team=` 진입 1회에 이 엔드포인트만 8회, 카드 전환 2회 포함 시 14회.
 * per-IP 60/분 예산(엣지 주소 하나로 합산)에서 이 중복이 예산의 큰 몫을 먹고 있었다.
 *
 * 멤버 변경(추가·역할변경·제거)은 아래 mutate 함수들이 해당 팀 캐시를 무효화하므로
 * 화면에서 바뀐 결과가 늦게 보이는 일은 없다. 그 외 경로의 stale 은 최대 60초.
 */
const MEMBERS_TTL_MS = 60_000;
const membersCache = new Map<string, { at: number; data: TeamMemberDto[] }>();
const membersInFlight = new Map<string, Promise<TeamMemberDto[]>>();

/** 멤버 목록이 바뀌는 작업 뒤 캐시를 버린다. */
export function invalidateTeamMembers(id?: string): void {
  if (id === undefined) { membersCache.clear(); return; }
  membersCache.delete(id);
}

export async function listTeamMembers(id: string): Promise<TeamMemberDto[]> {
  const hit = membersCache.get(id);
  if (hit && Date.now() - hit.at < MEMBERS_TTL_MS) return hit.data;

  const inflight = membersInFlight.get(id);
  if (inflight) return inflight;

  const p = (async () => {
    const res = await apiClient.get<{ members: TeamMemberDto[] }>(`/project-teams/${id}/members`);
    const data = (Array.isArray(res.data?.members) ? res.data.members : []).map(resolveMemberAvatar);
    membersCache.set(id, { at: Date.now(), data });
    return data;
  })().finally(() => { membersInFlight.delete(id); });

  membersInFlight.set(id, p);
  return p;
}

export async function addTeamMember(
  id: string,
  input: { userId: string; role?: ProjectTeamRole },
): Promise<TeamMemberDto> {
  const res = await apiClient.post<{ member: TeamMemberDto }>(`/project-teams/${id}/members`, input);
  invalidateTeamMembers(id);
  return resolveMemberAvatar(res.data.member);
}

export async function removeTeamMember(id: string, userId: string): Promise<void> {
  await apiClient.delete(`/project-teams/${id}/members/${userId}`);
  invalidateTeamMembers(id);
}

export async function patchTeamMemberRole(
  id: string,
  userId: string,
  role: ProjectTeamRole,
): Promise<{ userId: string; role: ProjectTeamRole }> {
  const res = await apiClient.patch<{ member: { userId: string; role: ProjectTeamRole } }>(
    `/project-teams/${id}/members/${userId}`,
    { role },
  );
  invalidateTeamMembers(id);
  return res.data.member;
}

export async function searchInviteCandidates(id: string, q: string, limit = 10): Promise<CandidateDto[]> {
  const res = await apiClient.get<{ candidates: CandidateDto[] }>(`/project-teams/${id}/candidates`, {
    params: { ...(q ? { q } : {}), limit },
  });
  return (Array.isArray(res.data?.candidates) ? res.data.candidates : []).map(resolveMemberAvatar);
}

// ★ 로그아웃하면 비운다 (2026-08-31) — **볼 수 있는 팀이 사람마다 다르다**.
registerSessionCache(() => { membersCache.clear(); });
